/**
 * A mensagem que a tela de login mostra quando a entrada falha.
 *
 * ⚠ Existe porque o `catch` do SignIn mostrava SEMPRE "Verifique seu e-mail e senha", qualquer que
 * fosse o erro — o que era inofensivo enquanto credencial errada era a única recusa possível, e
 * passou a mentir quando o backend ganhou a recusa por **colaborador desligado** (25/09/2026). A
 * pessoa lia "verifique sua senha", tentava outras, e só então ligava no suporte — com um problema
 * imaginário. O servidor já mandava a frase certa; a tela a jogava fora.
 */
export const MENSAGEM_GENERICA =
  "Não foi possível entrar. Verifique seu e-mail e senha e tente novamente.";

/** 429 sem `message` no corpo (proxy, ou backend que responde vazio): ainda assim não é senha errada. */
export const MENSAGEM_MUITAS_TENTATIVAS = "Muitas tentativas. Aguarde um minuto e tente de novo.";

/**
 * Só aproveita a mensagem do servidor quando ela vem como `{ message: "..." }` — que é a forma que
 * o backend usa para as recusas **escritas para o usuário** (acesso desativado, sem vínculo,
 * cadastro ambíguo).
 *
 * Corpo em texto cru é descartado de propósito: o 400 de credencial responde
 * `"Username or password incorrect"`, em inglês e no vocabulário do sistema. Repassar isso seria
 * pior que a frase genérica em português — e "mostrar o que o servidor mandar" viraria um canal por
 * onde qualquer mensagem interna futura vaza para a tela de login.
 */
export function mensagemDeErroDeLogin(error: unknown): string {
  const resp = (error as { response?: { status?: number; data?: unknown } } | null | undefined)?.response;
  const data = resp?.data;

  if (data && typeof data === "object") {
    const message = (data as { message?: unknown }).message;
    if (typeof message === "string" && message.trim().length > 0) return message;
  }

  return resp?.status === 429 ? MENSAGEM_MUITAS_TENTATIVAS : MENSAGEM_GENERICA;
}

/**
 * O login responde 403 `{ mustChangePassword: true }` quando a senha está certa mas é a temporária
 * (reset feito pelo admin). Não é falha: é o desvio para a tela de troca.
 */
export function exigeTrocaDeSenha(error: unknown): boolean {
  const resp = (error as { response?: { status?: number; data?: unknown } } | null | undefined)?.response;
  return (
    resp?.status === 403 &&
    !!resp.data &&
    typeof resp.data === "object" &&
    (resp.data as { mustChangePassword?: unknown }).mustChangePassword === true
  );
}
